import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameCard from '@components/GameCard';
import { searchGames } from '@lib/content';
import { getPrimaryCategories } from '@lib/game-utils';

const SearchPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = params.get('q') ?? '';
  const [query, setQuery] = useState(initialQuery);

  const primaryCategories = useMemo(() => getPrimaryCategories(), []);
  const results = useMemo(() => searchGames(query), [query]);

  useEffect(() => {
    const next = params.get('q') ?? '';
    setQuery(next);
  }, [params]);

  const submitQuery = (nextQuery: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextQuery) {
        next.set('q', nextQuery);
      } else {
        next.delete('q');
      }
      return next;
    });
  };

  const onSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();
    submitQuery(query);
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  };

  return (
    <>
      <SeoHead
        title={query ? `Search: ${query}` : 'Search games'}
        description="Find games instantly across categories with a static index."
        path="/search"
      />
      <section className="masthead">
        <div className="container">
          <div className="row mb-2">
            <div className="text-center mx-auto">
              <h1 className="display-6 fw-bold">Search games</h1>
              <h2>Find a title in the catalog and open it in the browser.</h2>
            </div>
          </div>
          <div className="search-container">
            <form className="mb-3 search-box" onSubmit={onSubmit}>
              <input
                type="text"
                className="form-control search-input"
                id="search-bar"
                name="search"
                placeholder="Search games..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button className="btn btn-primary ms-2 search-btn" id="search-button" type="submit" aria-label="Search site">
                <svg className="bi bi-search" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"></path>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="page-body">
        <div className="container">
          <div className="category-pills">
            {primaryCategories.map((cat) => (
              <Link key={cat.slug} className="category-pill" to={`/category/${cat.slug}`}>
                {cat.name}
              </Link>
            ))}
          </div>

          <section className="my-5 game-section" id="Featured">
            <h2 className="text-center section-title">SEARCH GAMES</h2>
            <h3 id="searchError" className={results.length === 0 && query ? '' : 'd-none'}>
              <i className="fa fa-ban"></i>
              <span>&nbsp;Not Found!</span>
            </h3>
            <div className="row">
              {results.map((game) => (
                <div key={game.slug} className="game-item col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2">
                  <GameCard game={game} />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default SearchPage;
